import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect, useCallback } from "react";
import { Toast } from "primereact/toast";
import { setPage } from "store/delvieryOrderSlice";
import MonthFilter from "components/Ui/MonthFilter/MonthFilter";
import DateTimeFilter from "components/Ui/DateTimeFilter/DateTimeFilter";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import { useSearchParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import Order from "./Order/Order";
import PercentageSummaryCard from "components/Ui/PercentageSummaryCard/PercentageSummaryCard";
import useCommissionDefaults from "hooks/useCommissionDefaults";
import { useGetVendorsDropdown } from "hooks/useVendors";
import { useGetUsersDropdown, userDropdownKeys } from "hooks/useUser";
import Select from "components/Ui/SelectField/SelectField";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useDeliveryOrder from "hooks/useDeliveryOrder";
import { updateDeliveryShippingPercentage } from "services/user";
import {
    formatStatNumber,
    getBaselinePercentage,
    parsePercentageInput,
    toPercentageInputValue,
} from "utils/percentage";
import { formatMoney } from "utils/money";
import { readApiError } from "utils/apiError";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { ORDER_STATUS_CHOICES as status } from "constants/orderStatus";

/**
 * Column set for the driver ledger. Deliberately narrower than
 * Order/Order/orderColumns.js: no `isFree` (a points-funded order is a customer
 * concern) and no `orderRating`, but both `deletedFor*` flags and the wallet
 * settlement time are kept so an operator can see the settlement state.
 * Column order and visibility are persisted by
 * useTableColumnsConfig in localStorage (per browser, not per account) under
 * the "delivery-orders" key — a separate key from the /orders table's
 * "orders", so the two layouts are independent.
 */
const COLUMNS = [
    { id: "id", label: "#", width: "10px" },
    { id: "name", label: "الاسم", width: "100px" },
    { id: "vendor", label: "المطعم", width: "80px" },
    { id: "status", label: "الحاله", width: "100px" },
    { id: "delivery", label: "السائق", width: "100px" },
    { id: "type", label: "نوع الطلب", width: "100px" },
    { id: "prepTime", label: "وقت التحضير", width: "100px" },
    // THE RED TIMER (admin#40), beside the time it counts down. Live while the
    // order waits for its driver, then gone: the column after it says how the
    // pickup went against the same time.
    {
        id: "prepTimer",
        label: "الوقت المتبقي",
        hint: "يعد تنازليًا من وقت التحضير، ثم بالسالب بالأحمر حتى يستلم السائق الطلب من المطعم",
        width: "110px",
    },
    // Same three as the orders page, and they matter more here: this is the
    // screen the driver's month-end count is read off, so the delivery charge
    // the commission is computed from belongs beside it.
    {
        id: "pickupDelta",
        label: "الاستلام من المطعم",
        hint: "هل استلم السائق الطلب من المطعم قبل انتهاء وقت التحضير أم بعده",
        width: "140px",
    },
    // Two durations that end at the same moment and start at different ones.
    // The first includes the wait at the restaurant; the second is the road
    // only. The labels are the ones the order page uses for the same numbers.
    {
        id: "deliveryTime",
        label: "من الإسناد حتى التسليم",
        hint: "المدة من إسناد الطلب للسائق حتى تسليمه للزبون، ومعها الانتظار في المطعم",
        width: "150px",
        wrap: true,
    },
    {
        id: "roadTime",
        label: "من استلام الطلب من المطعم حتى التسليم",
        hint: "المدة من استلام السائق للطلب من المطعم حتى تسليمه للزبون: وقت الطريق فقط",
        width: "170px",
        wrap: true,
    },
    { id: "area", label: "المنطقة", width: "100px" },
    { id: "shipping", label: "التوصيل (₪)", width: "90px" },
    { id: "total", label: "المجموع (₪)", width: "80px" },
    { id: "createdAt", label: "وقت الانشاء", width: "100px" },
    { id: "deletedForVendor", label: "محذوف للمطعم", width: "80px" },
    { id: "deletedForDelivery", label: "محذوف للسائق", width: "80px" },
    // THE TWO WALLETS, the same ids, labels and order as the /orders table
    // (admin#41): the restaurant, then the driver, like the two flags before
    // them. The driver cell keeps the settlement time as its second line.
    { id: "vendorWallet", label: "محفظة المطعم", width: "110px" },
    { id: "driverWallet", label: "محفظة السائق", width: "140px" },
    { id: "actions", label: "الاعدادات", width: "50px" },
];

/**
 * /deliveryOrders — the driver-audience ledger ("طلبات السائقين"). "Delivery"
 * here means the delivery-DRIVER view, NOT type="delivery" orders: the page
 * fetches the same GET api/order dataset as /orders with no type filter, so
 * normal, delivery and whatsapp orders all appear intermixed.
 *
 * What differs from /orders is the default soft-delete filter
 * (deletedForDelivery=false instead of deletedForVendor=false), the columns,
 * and the actions: this page carries the driver-settlement tools — filter by
 * driver + date range, the نسبة السائق percentage card computed over total
 * shipping, and the bulk "حذف طلبات السائقين" soft-delete used once a driver
 * is settled. Driver ASSIGNMENT is not done here; it lives on the /orders
 * rows (react-select → PATCH api/order/:id).
 *
 * AUDIENCE AND ACCESS: the route `/deliveryOrders` is wrapped in a bare
 * `<ProtectedRoute>` with NO permission condition (App.js), unlike `orders/*`
 * which requires `super_admin || roles.manage_orders`. Any authenticated
 * dashboard user — including a vendor-role restaurant owner — can open this
 * screen, and it has almost none of the role forks /orders has: the driver
 * filter, the city filter for super admins, the deleted filter, the نسبة
 * السائق card and the bulk delete are all rendered unconditionally. Only the
 * per-row "حذف للسائق فقط" item and the customer-note badge check the role.
 * Treat that as a documented constraint of this screen, not an intent.
 *
 * SETTLEMENT MODEL — the screen combines wallet and manual settlement:
 * 1. filter to one driver and a date window;
 * 2. read إجمالي الشحن (the API's `totalShipping` over the whole filtered set,
 *    not the visible page) from the stat boxes;
 * 3. the نسبة السائق card multiplies that by the percentage to show what the
 *    driver is owed. That product is displayed and discarded — it is never
 *    posted, never stored, and never linked to an order. Only the PERCENTAGE
 *    can be saved ("حفظ للسائق"), and saving it merely stores a per-driver
 *    default for next time;
 * 4. The wallet debits when the driver claims an order. Completion then writes
 *    `delivery_wallet_settled_at` and hides the order from this view.
 * 5. Cancellation or reassignment refunds the debit before completion, so the
 *    order remains eligible for the manual settlement flow.
 * 6. "حذف طلبات السائقين" soft-deletes the remaining filtered orders for the
 *    delivery audience. That flag records manual settlement.
 *
 * KNOWN CONSTRAINTS (documented, not fixed):
 * - The city filter reads `state.city.cities` straight from Redux and this
 *   screen never fetches cities (contrast /orders, which calls `useCity`). On a
 *   direct load or refresh of /deliveryOrders the slice is empty and the filter
 *   offers only "الكل"; it fills in only after some other screen that does
 *   fetch cities has been mounted.
 * - Every filter control here is UNCONTROLLED (`defaultValue`), where /orders
 *   uses `value` for the vendor and status pickers. Browser back/forward or any
 *   other change to the URL therefore updates the results without resyncing the
 *   widgets, so the controls can disagree with the rows on screen.
 * - Two effects both depend on `buildOrderParams`, so each filter change issues
 *   two GETs (one via `fetchOrders`, one via `refetchOrders`).
 * - The bulk delete's scope is the entire filtered set across all pages, not
 *   the twelve visible rows; it is gated only by requiring an active driver
 *   filter.
 * - The status list comes from constants/orderStatus.js so every order surface
 *   receives the same API value and Arabic label.
 */
const DeliveryOrders = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCity = searchParams.get("city");
    const resolvedCityId = adminInfo?.super_admin ? selectedCity || undefined : adminInfo?.cityId;
    // The rate that applies before anything is picked. The dashboard holds no
    // default of its own — see services/commissionDefaults.js.
    const { data: commissionDefaults } = useCommissionDefaults(resolvedCityId);

    const { vendors } = useGetVendorsDropdown({
        cityId: resolvedCityId,
    });
    const { users } = useGetUsersDropdown({
        role: "delivery",
        cityId: resolvedCityId,
    });
    const queryClient = useQueryClient();
    // Empty until a driver is chosen: the rate now comes from the API, not from
    // a constant this app keeps.
    const [percentage, setPercentage] = useState("");
    const [persistedPercentage, setPersistedPercentage] = useState(null);
    const [savingPercentage, setSavingPercentage] = useState(false);
    const [deleteAllOrders, setDeleteAllOrders] = useState(false);
    const [deleteForType, setDeleteForType] = useState(null);
    const [singleSoftDeleteFor, setSingleSoftDeleteFor] = useState(null);
    const [showDelete, setShowDelete] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const {
        loading,
        fetchOrders,
        fetchOrderStats,
        deleteOrder,
        deleteOrders,
        softDeleteOrder,
        editOrder,
        refetchOrders,
    } = useDeliveryOrder();

    // Show the full-page spinner only on the first load. On later refetches
    // (search/filter/pagination) keep the list — and the search box — mounted
    // so submitting a search doesn't unmount and refocus the input.
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!loading) setHasLoaded(true);
    }, [loading]);

    const page = useSelector((state) => state.deliveryOrder.page);
    const data = useSelector((state) => state.deliveryOrder.orders);
    const pages = useSelector((state) => state.deliveryOrder.pages);
    const count = useSelector((state) => state.deliveryOrder.count);
    const total = useSelector((state) => state.deliveryOrder.total);
    const shipping = useSelector((state) => state.deliveryOrder.shipping);
    const cities = useSelector((state) => state.city.cities);
    const dispatch = useDispatch();
    const toast = useRef(null);
    const [params, setParams] = useState(null);
    const previousParamsKey = useRef(null);
    const previousStatsParamsKey = useRef(null);
    const previousNewOrder = useRef(props.newOrder);
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("delivery-orders", COLUMNS);

    const buildOrderParams = useCallback(() => {
        const nextParams = {
            page,
            size: 12,
        };

        if (searchParams.get("vendor")) {
            nextParams.vendorId = searchParams.get("vendor");
        }
        if (searchParams.get("delivery")) {
            nextParams.deliveryId = searchParams.get("delivery");
        }
        if (searchParams.get("status")) {
            nextParams.status = searchParams.get("status");
        }
        if (searchParams.get("startDate")) {
            nextParams.startDate = searchParams.get("startDate");
        }
        if (searchParams.get("endDate")) {
            nextParams.endDate = searchParams.get("endDate");
        }
        if (searchParams.get("search")) {
            nextParams.search = searchParams.get("search");
        }
        if (resolvedCityId) {
            nextParams.cityId = resolvedCityId;
        }

        const deletedFilter = searchParams.get("deletedForDelivery");
        if (deletedFilter === "true") {
            nextParams.deletedForDelivery = "true";
        } else if (deletedFilter !== "all") {
            nextParams.deletedForDelivery = "false";
        }

        return nextParams;
    }, [page, resolvedCityId, searchParams]);

    const selectedDelivery = users?.find(
        (user) => String(user.id) === String(searchParams.get("delivery")),
    );
    const selectedDeliveryPersistedPercentage = parsePercentageInput(
        selectedDelivery?.shipping_percentage,
    );
    // What the server resolved — his own rate, or his city's default.
    const selectedDeliveryEffectivePercentage = selectedDelivery?.effective_shipping_percentage;
    const currentPercentage = parsePercentageInput(percentage);
    const percentageBase = Number(shipping || 0);
    const percentageAmount = (percentageBase * (currentPercentage ?? 0)) / 100;
    const percentageBaseline = getBaselinePercentage(
        persistedPercentage,
        selectedDeliveryEffectivePercentage,
    );
    const isPercentageDirty =
        currentPercentage !== null && currentPercentage !== percentageBaseline;
    const canPersistPercentage =
        Boolean(selectedDelivery?.id) &&
        currentPercentage !== null &&
        (persistedPercentage === null || isPercentageDirty);
    const percentageHint = !selectedDelivery?.id
        ? "يمكنك تعديل القيمة مؤقتاً الآن. اختر سائقاً أولاً إذا أردت حفظها كقيمة افتراضية."
        : currentPercentage === null
          ? "أدخل نسبة صالحة بين 0 و100 للحفظ."
          : persistedPercentage !== null && !isPercentageDirty
            ? "هذه القيمة مطابقة للمحفوظ حالياً. عدّلها إذا أردت حفظ قيمة جديدة."
            : "التعديل يعمل فوراً في هذه الشاشة فقط، ولن يُحفظ إلا بعد الضغط على زر الحفظ.";

    useEffect(() => {
        setPersistedPercentage(selectedDeliveryPersistedPercentage);
        setPercentage(
            toPercentageInputValue(
                getBaselinePercentage(
                    selectedDeliveryPersistedPercentage,
                    selectedDeliveryEffectivePercentage,
                ),
            ),
        );
    }, [selectedDelivery?.id, selectedDeliveryPersistedPercentage]);

    useEffect(() => {
        const newParams = buildOrderParams();
        const nextParamsKey = JSON.stringify(newParams);
        const statsParams = { ...newParams };
        delete statsParams.page;
        delete statsParams.size;
        const nextStatsParamsKey = JSON.stringify(statsParams);
        const paramsChanged = previousParamsKey.current !== nextParamsKey;
        const statsParamsChanged = previousStatsParamsKey.current !== nextStatsParamsKey;
        const newOrderChanged = previousNewOrder.current !== props.newOrder;

        previousParamsKey.current = nextParamsKey;
        previousStatsParamsKey.current = nextStatsParamsKey;
        previousNewOrder.current = props.newOrder;
        setParams(newParams);

        if (statsParamsChanged) {
            fetchOrderStats(statsParams);
        }

        if (paramsChanged) {
            fetchOrders(newParams);
            return;
        }

        if (newOrderChanged) {
            refetchOrders(newParams);
        }
    }, [buildOrderParams, fetchOrderStats, fetchOrders, props.newOrder, refetchOrders]);

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const handleSearch = (value) => {
        if (value) {
            searchParams.set("search", value);
        } else {
            searchParams.delete("search");
        }
        setSearchParams(searchParams);
        dispatch(setPage(1));
    };

    const setSelectedOrderValue = (vendor) => {
        setSelectedOrder(vendor);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor(null);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    const confirmDelete = async () => {
        // deleteOrder returns `true`, or the axios error. The error object is
        // truthy, so `if (response)` read every failure as a success — a
        // refused delete said «تم حذف الطلب بنجاح» (admin#70). The API refuses a
        // running order with 409 and says why; the dialog stays open.
        const response = await deleteOrder(selectedOrder.id);
        const failure = readApiError(response, "تعذّر حذف الطلب");
        if (failure) {
            showMessage("error", "هناك خطأ", failure);
            return;
        }
        showMessage("warn", "تم الحذف", "تم حذف الطلب بنجاح");
        closeDeleteDialog();
    };

    const handlePercentageChange = (event) => {
        const nextValue = event.target.value;

        if (nextValue === "") {
            setPercentage("");
            return;
        }

        const normalizedValue = parsePercentageInput(nextValue);

        if (normalizedValue === null) {
            return;
        }

        setPercentage(toPercentageInputValue(normalizedValue));
    };

    const resetPercentage = () => {
        setPercentage(
            toPercentageInputValue(
                getBaselinePercentage(persistedPercentage, selectedDeliveryEffectivePercentage),
            ),
        );
    };

    /**
     * Saves the typed percentage as this driver's stored default
     * (PATCH delivery/:userId/shipping-percentage).
     *
     * Only the percentage is persisted — never `percentageAmount`, the figure
     * the operator actually pays out, and nothing ties it to the orders in the
     * current filter. Requires a driver to be selected, since the value is
     * per-driver.
     */
    const persistPercentage = async () => {
        if (!selectedDelivery?.id || currentPercentage === null) {
            return;
        }

        setSavingPercentage(true);

        try {
            const response = await updateDeliveryShippingPercentage(
                selectedDelivery.id,
                currentPercentage,
            );
            const nextPersistedPercentage = parsePercentageInput(
                response.shipping_percentage ?? currentPercentage,
            );

            setPersistedPercentage(nextPersistedPercentage);
            setPercentage(
                toPercentageInputValue(
                    getBaselinePercentage(
                        nextPersistedPercentage,
                        selectedDeliveryEffectivePercentage,
                    ),
                ),
            );
            await queryClient.invalidateQueries({ queryKey: userDropdownKeys.all });
            showMessage("success", "تم الحفظ", "تم حفظ نسبة السائق بنجاح");
        } catch (err) {
            showMessage(
                "error",
                "تعذر الحفظ",
                err.response?.data?.message || "حدث خطأ أثناء حفظ نسبة السائق",
            );
        } finally {
            setSavingPercentage(false);
        }
    };

    /**
     * "حذف طلبات السائقين" — closes a driver settlement by hiding every order
     * matching the CURRENT filters from the driver ledger. The soft-delete flag
     * is the only trace that the driver was paid for that window.
     *
     * CONSTRAINT: scope is `params` — the whole filtered set across every page,
     * not the rows on screen. Forgetting the date range clears a driver's
     * entire history in one click; the only guard is that the button renders
     * solely while a driver filter is active.
     */
    const deleteAll = async () => {
        try {
            const response = await deleteOrders({ ...params, deleteFor: deleteForType });

            if (response?.ok) {
                // api#167: running orders are never swept. Say how many stayed.
                const kept = response.skippedRunning
                    ? ` — لم تُحذف ${response.skippedRunning} من الطلبات الجارية، ألغِها أولًا`
                    : "";
                showMessage("warn", "تم الحذف", `تم حذف الطلبات بنجاح${kept}`);
                closeDeleteDialog();
            } else {
                showMessage("error", "هناك خطأ", response?.message || "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error deleting order:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    /**
     * Soft-deletes a single order from delivery view only, while keeping it for other audiences.
     *
     * @param {object} order - Order row payload.
     */
    const deleteSingleForDelivery = async (order) => {
        if (!order) {
            showMessage("error", "هناك خطأ", "لا يوجد طلب محدد");
            return;
        }

        try {
            const response = await softDeleteOrder(order.id, "delivery");

            if (response?.ok) {
                showMessage("warn", "تم الحذف", "تم حذف الطلب للسائق");
                closeDeleteDialog();
                return;
            }

            // api#167: a running order is refused with its reason.
            showMessage("error", "لم يُحذف الطلب", response?.message || "حدث خطأ غير متوقع");
        } catch {
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const requestSingleSoftDeleteForDelivery = (order) => {
        setSelectedOrder(order);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor("delivery");
        setShowDelete(true);
    };

    const confirmSingleSoftDelete = async () => {
        await deleteSingleForDelivery(selectedOrder);
    };

    const closeDeleteDialog = () => {
        setShowDelete(false);
        setDeleteAllOrders(false);
        setDeleteForType(null);
        setSingleSoftDeleteFor(null);
    };

    const vendorOptions = vendors?.map((v) => ({
        value: v.id,
        label: v.name,
    }));

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {loading && !hasLoaded ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3 filters orders-filters">
                        <div className="col">
                            <h6>المطاعم</h6>
                            <Select
                                options={[{ value: "", label: "الكل" }, ...vendorOptions]}
                                placeholder="اختر المطعم"
                                defaultValue={
                                    vendorOptions.find(
                                        (v) => v.value === +searchParams.get("vendor"),
                                    ) || { value: "", label: "الكل" }
                                }
                                isSearchable
                                onChange={(selected) => {
                                    if (!selected || selected.value === "") {
                                        searchParams.delete("vendor");
                                    } else {
                                        searchParams.set("vendor", selected.value);
                                    }
                                    setSearchParams(searchParams);
                                }}
                            />
                        </div>
                        <div className="col">
                            <h6>السائق</h6>
                            <Form.Select
                                defaultValue={searchParams.get("delivery") || ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        searchParams.delete("delivery");
                                        setSearchParams(searchParams);
                                    } else {
                                        searchParams.set("delivery", e.target.value);
                                        setSearchParams(searchParams);
                                    }
                                }}
                            >
                                <option value={""}>الكل</option>
                                {users?.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        {adminInfo?.super_admin ? (
                            <div className="col">
                                <h6>المدينه</h6>
                                <Form.Select
                                    defaultValue={searchParams.get("city") || ""}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            searchParams.delete("city");
                                            setSearchParams(searchParams);
                                        } else {
                                            searchParams.set("city", e.target.value);
                                            setSearchParams(searchParams);
                                        }
                                    }}
                                >
                                    <option value={""}>الكل</option>
                                    {cities?.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        ) : null}
                        <div className="col">
                            <h6>الحاله</h6>
                            <Form.Select
                                defaultValue={searchParams.get("status") || ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        searchParams.delete("status");
                                        setSearchParams(searchParams);
                                    } else {
                                        searchParams.set("status", e.target.value);
                                        setSearchParams(searchParams);
                                    }
                                }}
                            >
                                <option value={""}>الكل</option>
                                {status?.map((s) => (
                                    <option key={s.en} value={s.en}>
                                        {s.ar}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="col">
                            <h6>الطلبات المحذوفة</h6>
                            <Form.Select
                                defaultValue={searchParams.get("deletedForDelivery") || ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        searchParams.delete("deletedForDelivery");
                                    } else {
                                        searchParams.set("deletedForDelivery", e.target.value);
                                    }
                                    setSearchParams(searchParams);
                                }}
                            >
                                <option value={""}>إخفاء المحذوفة</option>
                                <option value="all">عرض الطلبات المحذوفة ايضا</option>
                                <option value="true">عرض الطلبات المحذوفة فقط</option>
                            </Form.Select>
                        </div>
                        <MonthFilter
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                        <DateTimeFilter
                            paramKey="startDate"
                            label="من"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                        <DateTimeFilter
                            paramKey="endDate"
                            label="الى"
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            onApplied={() => dispatch(setPage(1))}
                        />
                    </div>
                    <div className="stats-container2">
                        <div className="stat-box">
                            <h4>عدد الطلبات</h4>
                            <p>{formatStatNumber(count)}</p>
                        </div>

                        <div className="stat-box">
                            <h4>الإجمالي (₪)</h4>
                            <p>{formatMoney(total)}</p>
                        </div>

                        <div className="stat-box">
                            <h4>إجمالي الشحن (₪)</h4>
                            <p>{formatMoney(shipping)}</p>
                        </div>
                    </div>
                    <div className="percentage-panel-row">
                        <PercentageSummaryCard
                            title="نسبة السائق"
                            contextName={selectedDelivery?.name}
                            savedPercentage={persistedPercentage}
                            baselineValue={percentageBaseline}
                            fallbackValue={commissionDefaults?.driver ?? null}
                            currentValue={percentage}
                            onChange={handlePercentageChange}
                            onReset={resetPercentage}
                            onPersist={persistPercentage}
                            isSaving={savingPercentage}
                            canPersist={canPersistPercentage}
                            saveLabel="حفظ للسائق"
                            emptySelectionHint="اختر سائقاً لحفظ النسبة"
                            basisLabel="إجمالي الشحن"
                            basisValue={percentageBase}
                            resultValue={percentageAmount}
                            isDirty={isPercentageDirty}
                            inputId="delivery-order-percentage"
                            hintText={percentageHint}
                        />
                    </div>
                    <div className="controls">
                        <SearchInput
                            initialValue={searchParams.get("search") || ""}
                            onSearch={handleSearch}
                            placeholder="بحث بالاسم، رقم الطلب أو الهاتف"
                            minWidth={330}
                        />
                        <div style={{ display: "flex", gap: "10px", marginRight: "auto" }}>
                            {params?.deliveryId ? (
                                <button
                                    onClick={() => {
                                        setDeleteAllOrders(true);
                                        setDeleteForType("delivery");
                                        toggleDelete();
                                    }}
                                    style={{
                                        backgroundColor: "red",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} />
                                    حذف طلبات السائقين
                                </button>
                            ) : null}
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <TableHead visibleColumns={visibleColumns} />
                            {data.length ? (
                                <tbody>
                                    {data.map((order) => (
                                        <Order
                                            key={order.id}
                                            order={order}
                                            visibleColumns={visibleColumns}
                                            setSelectedOrder={setSelectedOrder}
                                            setSelectedOrderValue={setSelectedOrderValue}
                                            toggleDelete={toggleDelete}
                                            editOrder={editOrder}
                                            showMessage={showMessage}
                                            onRequestDeleteForDelivery={
                                                requestSingleSoftDeleteForDelivery
                                            }
                                        />
                                    ))}
                                </tbody>
                            ) : (
                                <h4>لا يوجد طلبات!</h4>
                            )}
                        </table>
                    </div>
                    <Stack spacing={1}>
                        <Pagination
                            count={pages}
                            page={page}
                            shape="rounded"
                            color={BRAND}
                            style={{ margin: "20px auto 0" }}
                            onChange={handleChangePage}
                        />
                    </Stack>
                    <DeleteItem
                        show={showDelete}
                        close={closeDeleteDialog}
                        loading={loading}
                        confirm={
                            deleteAllOrders
                                ? deleteAll
                                : singleSoftDeleteFor === "delivery"
                                  ? confirmSingleSoftDelete
                                  : confirmDelete
                        }
                    />
                </>
            )}
        </div>
    );
};
export default DeliveryOrders;
